<script setup lang="ts">
import type { ChartHoverInfo, ChartMargin, ChartSeries } from './ocgChartRenderer';

import { computed, nextTick, onMounted, ref, watch } from 'vue';

import { renderOcgChart } from './ocgChartRenderer';

const props = withDefaults(
  defineProps<{
    series: ChartSeries[];
    nPhases: number;
    startPhase?: number;
    esIndices?: number[];
    edIndices?: number[];
    excludedPhaseIndices?: number[];
    currentPhaseIndex?: number;
    showEdEsMarkers?: boolean;
    width?: number;
    height?: number;
    margin?: ChartMargin;
    yMin?: number;
    yMax?: number;
    formatY?: (val: number) => string;
    formatX?: (val: number, startPhase: number) => string;
    triggerTimes?: number[];
    showTooltip?: boolean;
    emptyText?: string;
    clickable?: boolean;
  }>(),
  {
    startPhase: 0,
    esIndices: () => [],
    edIndices: () => [],
    excludedPhaseIndices: () => [],
    currentPhaseIndex: undefined,
    showEdEsMarkers: true,
    width: 440,
    height: 155,
    margin: () => ({ top: 16, right: 10, bottom: 26, left: 40 }),
    yMin: undefined,
    yMax: undefined,
    formatY: undefined,
    formatX: undefined,
    triggerTimes: () => [],
    showTooltip: true,
    emptyText: 'No curve data available',
    clickable: false,
  },
);

const emit = defineEmits<{
  (e: 'phaseClick', phase: number): void;
}>();

const chartSvg = ref<SVGSVGElement | null>(null);
const zoomDomain = ref<[number, number] | null>(null);
const hoverInfo = ref<ChartHoverInfo | null>(null);

const hasData = computed(() => {
  return props.nPhases > 0 && props.series.length > 0 && props.series.some(s => s.data.length > 0);
});

const tooltipPosition = computed(() => {
  if (!hoverInfo.value) return { x: 0, y: 0 };
  const snapX = hoverInfo.value.pixelX;
  const clampedX = Math.min(snapX + 10, props.width - 145);
  const clampedY = Math.max(props.margin.top, 18);
  return { x: clampedX, y: clampedY };
});

function resetZoom() {
  zoomDomain.value = null;
  draw();
}

function draw() {
  if (!chartSvg.value || !hasData.value) return;

  renderOcgChart({
    svgElement: chartSvg.value,
    series: props.series,
    nPhases: props.nPhases,
    startPhase: props.startPhase,
    esIndices: props.esIndices,
    edIndices: props.edIndices,
    excludedPhaseIndices: props.excludedPhaseIndices,
    currentPhaseIndex: props.currentPhaseIndex,
    showEdEsMarkers: props.showEdEsMarkers,
    width: props.width,
    height: props.height,
    margin: props.margin,
    zoomDomain: zoomDomain.value,
    yMin: props.yMin,
    yMax: props.yMax,
    formatY: props.formatY,
    formatX: props.formatX,
    onHover: (info) => {
      hoverInfo.value = info;
    },
    onZoomChange: (domain) => {
      zoomDomain.value = domain;
      draw();
    },
    onPhaseClick: props.clickable
      ? (phase) => {
          emit('phaseClick', phase);
        }
      : undefined,
  });
}

watch(
  [
    () => props.series,
    () => props.nPhases,
    () => props.startPhase,
    () => props.esIndices,
    () => props.edIndices,
    () => props.excludedPhaseIndices,
    () => props.currentPhaseIndex,
    () => props.showEdEsMarkers,
    () => props.width,
    () => props.height,
    () => props.yMin,
    () => props.yMax,
  ],
  () => {
    nextTick(() => draw());
  },
  { deep: true },
);

onMounted(() => {
  nextTick(() => draw());
});

defineExpose({
  resetZoom,
});
</script>

<template>
  <div class="ocg-curve-chart-container relative w-full flex flex-col select-none">
    <div v-if="!hasData" class="empty-chart-placeholder py-6 text-center text-xs text-slate-400">
      {{ emptyText }}
    </div>

    <div v-else class="relative w-full flex justify-center">
      <svg
        ref="chartSvg"
        :viewBox="`0 0 ${width} ${height}`"
        class="w-full h-auto max-h-[190px] block"
      />

      <!-- Zoom Reset Button (shows only when zoomed in) -->
      <button
        v-if="zoomDomain !== null"
        type="button"
        class="zoom-reset-badge absolute top-1 right-2 px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-800/80 border border-slate-600/60 text-cyan-300 hover:bg-slate-700 transition cursor-pointer shadow-sm z-10"
        title="Double-click chart or click here to reset zoom"
        @click.stop="resetZoom"
      >
        <i class="fa-solid fa-magnifying-glass-minus mr-1" />Reset zoom
      </button>

      <!-- Tooltip Overlay -->
      <div
        v-if="showTooltip && hoverInfo !== null"
        class="chart-tooltip-overlay absolute p-1.5 rounded bg-slate-900/95 border border-slate-700/80 text-[10px] pointer-events-none z-20 shadow-xl backdrop-blur-sm"
        :style="{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
        }"
      >
        <div class="font-bold text-white border-b border-white/10 pb-0.5 mb-1 flex items-center justify-between gap-2">
          <div class="flex items-center gap-1.5">
            <span>Phase {{ hoverInfo.phase + startPhase + 1 }}</span>
            <span
              v-if="hoverInfo.isEs"
              class="px-1 py-0.2 text-[8px] font-bold rounded bg-emerald-500/30 text-emerald-300 border border-emerald-500/40"
            >
              ES
            </span>
            <span
              v-if="hoverInfo.isEd"
              class="px-1 py-0.2 text-[8px] font-bold rounded bg-rose-500/30 text-rose-300 border border-rose-500/40"
            >
              ED
            </span>
          </div>
          <span
            v-if="triggerTimes && triggerTimes[hoverInfo.phase] !== undefined"
            class="text-slate-400 font-mono text-[9px]"
          >
            {{ triggerTimes[hoverInfo.phase] }}ms
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <div
            v-for="s in hoverInfo.values"
            :key="s.seriesId"
            class="flex items-center justify-between gap-2"
          >
            <span class="font-medium" :style="{ color: s.color }">{{ s.name }}:</span>
            <span class="font-mono text-slate-200">
              {{ formatY ? formatY(s.value) : s.value.toLocaleString(undefined, { maximumFractionDigits: 1 }) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ocg-curve-chart-container {
  overflow: hidden;
}

.chart-tooltip-overlay {
  min-width: 95px;
}
</style>
